/**
 * Authentication Service
 * Gestisce login, logout, token management
 */

import { BaseService, ApiError } from './BaseService';
import { LoginFormData, PatientInfo, LoginResponse } from '../../types';
import { sanitizeCodiceFiscale, validateCodiceFiscale } from '../../lib/validators';
import logger from '../Logger';
import auditLogger from '../AuditLogger';
import storageService from '../StorageService';
import ConfigService from '../ConfigService';

class AuthService extends BaseService {
  private static instance: AuthService;
  private token: string | null = null;
  private refreshToken: string | null = null;

  private constructor() {
    super();

    // Il proxy gestisce automaticamente i token client
    // Non è più necessario gestire token nel frontend
    console.log('🔐 AuthService initialized - using proxy for authentication');
  }

  /**
   * Singleton pattern
   */
  static getInstance(): AuthService {
    if (!AuthService.instance) {
      AuthService.instance = new AuthService();
    }
    return AuthService.instance;
  }

  /**
   * Login utente
   */
  async login(credentials: LoginFormData): Promise<LoginResponse> {
    // Sanitizza e valida il codice fiscale
    const sanitizedCF = sanitizeCodiceFiscale(credentials.username);
    
    if (!validateCodiceFiscale(sanitizedCF)) {
      throw this.createError('Codice Fiscale non valido', 400);
    }

    try {
      // Il proxy gestisce automaticamente i token client
      // Non è più necessario ottenere il token manualmente
      console.log('🔐 Login with proxy - no manual token needed');
      logger.info('Login with proxy - authentication handled by proxy server');

      // Effettua il login - il proxy aggiungerà automaticamente il Bearer token
      const fullResponse = await fetch(`${this.baseURL}api/Account/login`, {
        method: 'POST',
        headers: {
          ...this.defaultHeaders
        },
        body: JSON.stringify({
          username: sanitizedCF,
          password: credentials.password,
          rememberMe: credentials.rememberMe || false
        })
      });

      // Log tutti gli headers della response
      console.log('📦 Response headers:');
      fullResponse.headers.forEach((value, key) => {
        console.log(`  ${key}: ${value}`);
      });

      // Verifica lo status PRIMA di processare il JSON
      if (!fullResponse.ok) {
        const errorResponse = await fullResponse.json();
        console.log('❌ Login failed with status:', fullResponse.status);
        console.log('❌ Error response:', errorResponse);

        // Se è un errore 401 con messaggio di email non confermata, lancia l'errore appropriato
        if (fullResponse.status === 401) {
          let errorMessage = errorResponse.Messaggio || errorResponse.messaggio || errorResponse.message || errorResponse.error || 'Credenziali non valide';

          // Se l'utente non è ancora abilitato, usa il messaggio configurabile
          if (errorMessage.includes('non risulti ancora abilitato') ||
              errorMessage.includes('A partire da')) {
            errorMessage = ConfigService.getNoActivatedUserMessage();
          }

          // NON salvare il token se il login fallisce!
          // Rimuovi qualsiasi token esistente per prevenire accessi non autorizzati
          if (errorMessage.toLowerCase().includes('confermare') ||
              errorMessage.toLowerCase().includes('email')) {
            // Pulisci i token per essere sicuri
            this.clearTokens();
            throw this.createError(errorMessage, 401, errorResponse);
          }

          throw this.createError(errorMessage, 401, errorResponse);
        }

        throw this.createError(
          errorResponse.Messaggio || errorResponse.messaggio || errorResponse.message || `HTTP ${fullResponse.status}`,
          fullResponse.status,
          errorResponse
        );
      }

      const response = await fullResponse.json();
      console.log('📦 Login response body:', response);

      // IMPORTANTE: Solo se il login ha successo (status 200) gestiamo il token
      const userToken = response.token || (response as any).accessToken || (response as any).access_token;

      if (userToken) {
        // Se c'è un nuovo token, usalo
        this.token = userToken;
        console.log('✅ New user token saved, length:', userToken.length);
      } else {
        // Altrimenti mantieni il token client SOLO se il login ha avuto successo
        console.log('ℹ️ No token in login response, keeping client token');
        console.log('ℹ️ Current token will be used for API calls:', this.token ? `${this.token.substring(0, 20)}...` : 'NO TOKEN!');
      }

      if (response.refreshToken) {
        this.refreshToken = response.refreshToken;
      }

      logger.info('Login successful for user:', sanitizedCF);

      // Audit log for successful login
      auditLogger.logLogin(sanitizedCF);

      return response;
    } catch (error: any) {
      logger.error('Login failed:', error);

      // Gestione errori specifici
      if (error.statusCode === 401) {
        // Se l'errore è già formattato con il messaggio di email non confermata, lo rilanciamo così com'è
        if (error.message && (error.message.includes('confermare') || error.message.includes('email'))) {
          throw error;
        }
        // Se l'errore ha già dettagli dal backend, mantienili
        // Altrimenti passa un messaggio che l'ErrorHandler riconoscerà come errore di login
        if (error.details && (error.details.Messaggio || error.details.messaggio || error.details.message)) {
          throw error; // L'errore ha già i dettagli corretti dal backend
        }
        throw this.createError('Credenziali non valide', 401, { message: 'Credenziali non valide' });
      } else if (error.statusCode === 423) {
        throw this.createError('Account bloccato. Contatta l\'assistenza.', 423);
      }

      throw error;
    }
  }

  // getClientToken() RIMOSSA - Il proxy gestisce automaticamente i token client
  // Il frontend non ha più accesso diretto alle credenziali API

  /**
   * Logout utente
   */
  async logout(): Promise<void> {
    try {
      // Get user info before clearing tokens for audit log
      const codiceFiscale = storageService.getItem('codiceFiscale');
      
      if (this.token) {
        // Eventuale chiamata API per invalidare il token server-side
        // await this.post('api/Account/logout', {});
      }
      
      // Audit log for logout
      auditLogger.logLogout(codiceFiscale || undefined);
    } finally {
      this.clearTokens();
      logger.info('User logged out');
    }
  }

  /**
   * Recupera informazioni paziente
   */
  async getPatientInfo(codiceFiscale: string): Promise<PatientInfo> {
    if (!this.token) {
      throw this.createError('Non autenticato', 401);
    }

    const sanitizedCF = sanitizeCodiceFiscale(codiceFiscale);
    
    return this.withRetry(async () => {
      const response = await this.request<PatientInfo>(
        `user/patient/info?codiceFiscale=${encodeURIComponent(sanitizedCF)}`,
        {
          method: 'GET',
          headers: this.withAuth(this.token!)
        }
      );
      
      return response;
    });
  }

  /**
   * Cambio password
   */
  async changePassword(
    username: string,
    oldPassword: string,
    newPassword: string
  ): Promise<void> {
    if (!this.token) {
      throw this.createError('Non autenticato', 401);
    }

    const sanitizedCF = sanitizeCodiceFiscale(username);

    await this.post('api/Account/changePassword', {
      username: sanitizedCF,
      oldPassword,
      newPassword
    });

    logger.info('Password changed successfully');
  }

  /**
   * Recupero password
   */
  async forgotPassword(username: string): Promise<void> {
    const sanitizedCF = sanitizeCodiceFiscale(username);

    await this.post('api/Account/forgotPasswordForm', {
      username: sanitizedCF
    });

    logger.info('Password reset email sent');
  }

  /**
   * Reinvio email di conferma (non richiede autenticazione)
   */
  async resendConfirmationEmail(username: string): Promise<void> {
    const sanitizedCF = sanitizeCodiceFiscale(username);

    // Non richiede autenticazione, usa direttamente request senza token
    await this.request('api/Account/resendConfirmationEmail', {
      method: 'POST',
      body: JSON.stringify({
        username: sanitizedCF
      })
    });

    logger.info('Confirmation email resent');
  }

  /**
   * Verifica se l'utente esiste per codice fiscale
   * Backend: 200 = non esiste (può registrarsi), 400 = esiste già
   * Ritorna: { exists: boolean, error?: string }
   */
  async checkUserExists(codiceFiscale: string): Promise<{ exists: boolean; error?: string }> {
    const sanitizedCF = sanitizeCodiceFiscale(codiceFiscale);

    try {
      await this.post('api/Account/checkUserExists', { codiceFiscale: sanitizedCF });
      // 200 = utente NON esiste (può registrarsi)
      return { exists: false };
    } catch (error: any) {
      if (error.statusCode === 400) {
        // 400 = utente ESISTE già
        return { exists: true };
      }
      // Altri errori - restituisci l'errore ma permetti la registrazione
      return {
        exists: false,
        error: `Errore verifica utente (${error.statusCode || 'network'}): ${error.message || 'Errore sconosciuto'}`
      };
    }
  }

  /**
   * Registrazione nuovo utente
   */
  async register(data: {
    codiceFiscale: string;
    email: string;
    password: string;
    nome: string;
    cognome: string;
    telefono: string;
    birthdate: string;
    genere: boolean | null;
  }): Promise<void> {
    await this.post('api/Account/register', data);
    logger.info('User registered successfully:', data.codiceFiscale);
  }

  /**
   * Aggiorna email paziente
   */
  async updateEmail(codiceFiscale: string, newEmail: string): Promise<void> {
    if (!this.token) {
      throw this.createError('Non autenticato', 401);
    }

    const sanitizedCF = sanitizeCodiceFiscale(codiceFiscale);

    await this.post('user/patient/updateemail', {
      codiceFiscale: sanitizedCF,
      email: newEmail
    });

    logger.info('Email updated successfully');
  }

  /**
   * Refresh del token
   */
  async refreshAccessToken(): Promise<string> {
    if (!this.refreshToken) {
      throw this.createError('No refresh token available', 401);
    }

    try {
      const response = await this.post<LoginResponse>('api/Auth/refresh', {
        refreshToken: this.refreshToken
      });

      this.token = response.token;
      if (response.refreshToken) {
        this.refreshToken = response.refreshToken;
      }

      return response.token;
    } catch (error) {
      this.clearTokens();
      throw error;
    }
  }


  /**
   * Getters
   */
  getToken(): string | null {
    return this.token;
  }

  getRefreshToken(): string | null {
    return this.refreshToken;
  }

  setToken(token: string): void {
    this.token = token;
  }
  
  clearToken(): void {
    this.clearTokens();
  }

  isAuthenticated(): boolean {
    return !!this.token;
  }

  /**
   * Clear tokens
   */
  private clearTokens(): void {
    this.token = null;
    this.refreshToken = null;
    // Il proxy gestisce i token - non è più necessario pulire sessionStorage
  }

  /**
   * Helper per creare errori
   */
  protected createError(message: string, statusCode?: number, details?: any): ApiError {
    return super.createError(message, statusCode, details);
  }
}

export default AuthService.getInstance();